// Owner: shell-nav (plan §4.8, §9.2). The 52 px top bar of app pages: breadcrumb (the last crumb is the
// page), page status, the items features contribute (`features/**/topbar.ts`: the bell, presence …) and
// the page actions. Until a feature contributes the bell (`inbox.bell`), a plain bell links to the Inbox.
import { Link, type LinkProps } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';
import { Icon, buttonClass, useIsCompact } from '@dataroom/ui';
import { topbarItemsFor } from '../extensions/topbar';
import { t } from '../lib/i18n';
import { AskAiButton } from '../ai/AskAiButton';
import { useShellPathname } from './pathname';

export type Crumb = {
  label: string;
  to?: NonNullable<LinkProps['to']>;
  params?: Record<string, string>;
  /** Search params of the link (a Library folder: `{ parent: id }`). */
  search?: Record<string, string>;
};

export type TopbarProps = {
  crumbs: Crumb[];
  actions?: ReactNode;
  /** The notification bell (app pages; default true). */
  notifications?: boolean;
  /** Text / status between crumbs and actions (e.g. "All changes saved"). */
  status?: ReactNode;
};

/** Contributed items for a placement (rendered in order). */
export function TopbarItems({ placement }: { placement: 'start' | 'end' }) {
  const pathname = useShellPathname();
  const compact = useIsCompact();
  const items = topbarItemsFor(pathname, placement);
  if (!items.length) return null;
  return (
    <div className={`topbar-items topbar-items-${placement}`}>
      {items.map((i) => (
        <i.Component key={i.id} pathname={pathname} compact={compact} />
      ))}
    </div>
  );
}

export function Topbar({ crumbs, actions, notifications = true, status }: TopbarProps) {
  const pathname = useShellPathname();
  const contributedBell = topbarItemsFor(pathname, 'end').some((i) => i.id === 'inbox.bell');
  // Phones have no rail: the Copilot is one tap from every app page, not only inside the menu sheet.
  const compact = useIsCompact();
  return (
    <header className="topbar" data-testid="topbar">
      <nav className="crumb" aria-label={t('shell.breadcrumb')}>
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={`${c.label}-${i}`}>
              {i > 0 ? <span aria-hidden="true">/</span> : null}
              {last ? (
                <b aria-current="page">{c.label}</b>
              ) : c.to ? (
                <Link
                  to={c.to}
                  // A crumb names its route by data: `to` is checked against the route tree, while the router's
                  // per-route param and search types cannot follow a union of every route (library generics).
                  params={c.params as never}
                  search={c.search as never}
                  activeOptions={{ exact: true, includeSearch: Boolean(c.search) }}
                >
                  {c.label}
                </Link>
              ) : (
                <span>{c.label}</span>
              )}
            </Fragment>
          );
        })}
      </nav>
      <TopbarItems placement="start" />
      <div style={{ flexGrow: 1 }} />
      {status}
      {compact && notifications ? <AskAiButton /> : null}
      {notifications ? <TopbarItems placement="end" /> : null}
      {notifications && !contributedBell ? (
        <Link
          to="/inbox"
          className={buttonClass({ variant: 'ghost', size: 'sm', iconOnly: true })}
          aria-label={t('shell.notifications')}
          data-testid="topbar-bell"
        >
          <Icon name="bell" />
        </Link>
      ) : null}
      {actions}
    </header>
  );
}
